import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { ToastService } from "../services/toast.service";
import { ServerErrorCodes } from "@app/common/constants";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private toastService: ToastService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = "An unknown error occurred";

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          errorMessage = `Error: ${error.error.message}`;
        } else {
          const htmlErrorPayload = this.extractHtmlPayload(error.error);
          if (htmlErrorPayload) {
            this.toastService.errorHtml(htmlErrorPayload);
            return throwError(() => error);
          }

          // Server-side error
          if (error.status === 0) {
            errorMessage =
              "Unable to connect to the server. Please check your internet connection.";
          } else if (error.status === ServerErrorCodes.unauthorized) {
            errorMessage = `Unauthorized access. Please log in again. ${error.error?.detail}`;
          } else if (error.status === ServerErrorCodes.accessDeny) {
            errorMessage = `You do not have permission to access this resource. ${error.error?.detail}`;
          } else if (error.status === ServerErrorCodes.notFound) {
            errorMessage = "The requested resource was not found.";
          } else if (error.status === ServerErrorCodes.internalServer) {
            errorMessage = "Server error. Please try again later.";
          } else {
            // Try to extract a message from the error response (including HTML payloads).
            const parsedPayloadMessage = this.extractErrorMessage(error.error);
            errorMessage =
              parsedPayloadMessage ||
              error.message ||
              `Error ${error.status}: ${error.statusText}`;
          }
        }

        // Display the error in the toast
        this.toastService.error(errorMessage);

        // Continue with the error chain
        return throwError(() => error);
      })
    );
  }

  private extractHtmlPayload(payload: unknown): string | undefined {
    if (!payload) {
      return undefined;
    }

    if (typeof payload === "string") {
      const trimmed = payload.trim();
      return this.looksLikeHtml(trimmed) ? trimmed : undefined;
    }

    if (typeof payload === "object") {
      const obj = payload as Record<string, unknown>;
      const candidate = obj["message"] || obj["error"] || obj["detail"];

      if (typeof candidate === "string") {
        const trimmed = candidate.trim();
        return this.looksLikeHtml(trimmed) ? trimmed : undefined;
      }
    }

    return undefined;
  }

  private extractErrorMessage(payload: unknown): string | undefined {
    if (!payload) {
      return undefined;
    }

    if (typeof payload === "string") {
      const trimmed = payload.trim();

      return trimmed;
    }

    if (typeof payload === "object") {
      const obj = payload as Record<string, unknown>;
      const candidate = obj["message"] || obj["error"] || obj["detail"];

      if (typeof candidate === "string") {
        return candidate.trim();
      }
    }

    return undefined;
  }

  private looksLikeHtml(value: string): boolean {
    return /<\s*!doctype\s+html|<\s*html|<\s*body|<\s*title|<\s*h1/i.test(value);
  }
}
